import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import TopBar from '../components/ui/TopBar'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import texts from '../texts'
import { api, getMe } from '../lib/api'

interface Stats {
  usersCount: number
  gamesCount: number
  roundsCount: number
  visitsCount: number
}

interface AdminUser {
  id: number
  name: string
  email: string
  createdAt: string
}

function Admin() {
  const navigate = useNavigate()
  const [allowed, setAllowed] = useState<boolean | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [confirmingId, setConfirmingId] = useState<number | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    getMe()
      .then((me) => {
        if (!me.isAdmin) {
          navigate('/')
          return
        }
        setAllowed(true)
        api.get<Stats>('/stats').then((res) => setStats(res.data))
      })
      .catch(() => navigate('/'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadUsers = (nextPage: number, nextSearch: string) => {
    setLoadingUsers(true)
    api
      .get('/users', { params: { page: nextPage, search: nextSearch || undefined } })
      .then((res) => {
        setUsers(res.data.users ?? [])
        setPage(res.data.page ?? nextPage)
        setTotalPages(res.data.totalPages ?? 1)
      })
      .finally(() => setLoadingUsers(false))
  }

  useEffect(() => {
    if (allowed) loadUsers(1, '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed])

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    loadUsers(1, search)
  }

  const handleDelete = async (id: number) => {
    setError('')
    try {
      await api.delete(`/users/${id}`)
      setConfirmingId(null)
      loadUsers(page, search)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.errors.generic)
    }
  }

  if (!allowed) {
    return (
      <Screen>
        <p className="text-text-2">…</p>
      </Screen>
    )
  }

  return (
    <Screen>
      <TopBar title={texts.admin.title} back="/me" />

      {stats && (
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: texts.admin.usersTile, value: stats.usersCount },
            { label: texts.admin.gamesTile, value: stats.gamesCount },
            { label: texts.admin.roundsTile, value: stats.roundsCount },
            { label: texts.admin.visitsTile, value: stats.visitsCount }
          ].map((tile) => (
            <div key={tile.label} className="grid gap-1 rounded-2xl border border-text-2/20 bg-text-2/10 p-3.5">
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">{tile.label}</span>
              <b className="font-mono text-[1.4em]">{tile.value}</b>
            </div>
          ))}
        </div>
      )}

      <form className="flex gap-2" onSubmit={handleSearch}>
        <Input
          placeholder={texts.admin.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="submit" variant="ghost">
          {texts.admin.searchPlaceholder}
        </Button>
      </form>

      {error && <p className="text-red">{error}</p>}

      <div className="grid gap-1.5">
        {loadingUsers ? (
          <p className="text-text-2">…</p>
        ) : users.length === 0 ? (
          <p className="text-text-2">{texts.admin.empty}</p>
        ) : (
          users.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <b className="block">{user.name}</b>
                <span className="text-[0.8em] text-text-2">{user.email}</span>
              </div>
              {confirmingId === user.id ? (
                <div className="grid gap-1.5">
                  <span className="text-[0.8em] text-red">{texts.admin.deleteConfirm(user.name)}</span>
                  <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                      {texts.admin.cancel}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(user.id)}>
                      {texts.admin.deleteConfirmButton}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setConfirmingId(user.id)}>
                  {texts.admin.delete}
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => loadUsers(page - 1, search)}>
          {texts.admin.prev}
        </Button>
        <span className="text-[0.85em] text-text-2">{texts.admin.page(page, totalPages)}</span>
        <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => loadUsers(page + 1, search)}>
          {texts.admin.next}
        </Button>
      </div>
    </Screen>
  )
}

export default Admin
